import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";

const SupportSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    supportInfoID: 1,
    supportTitle: "",
    supportEmail: "",
    goFatafatSupportEmail: "",
    supportPhone: "",
    supportWebsite: "",
    supportHours: "",
    helpUrl: "",
    whatsAppNumber: "",
    supportMessage: "",
    isActive: true,
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SA_SUPPORT_INFO);
      if (response.data.status && response.data.data) {
        const data = response.data.data;
        setSettings({
          supportInfoID: data.supportInfoID ?? 1,
          supportTitle: data.supportTitle || "",
          supportEmail: data.supportEmail || "",
          goFatafatSupportEmail: data.goFatafatSupportEmail || "",
          supportPhone: data.supportPhone || "",
          supportWebsite: data.supportWebsite || "",
          supportHours: data.supportHours || "",
          helpUrl: data.helpUrl || "",
          whatsAppNumber: data.whatsAppNumber || "",
          supportMessage: data.supportMessage || "",
          isActive: data.isActive ?? true,
        });
      }
    } catch (err) {
      showErrorToast("Failed to load support settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!settings.supportEmail.trim()) {
      showErrorToast("Support email is required");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(APIs.PUT_SA_UPDATE_SUPPORT_INFO, {
        supportInfoID: 1,
        supportTitle: settings.supportTitle,
        supportEmail: settings.supportEmail,
        goFatafatSupportEmail: settings.goFatafatSupportEmail,
        supportPhone: settings.supportPhone,
        supportWebsite: settings.supportWebsite,
        supportHours: settings.supportHours,
        helpUrl: settings.helpUrl,
        whatsAppNumber: settings.whatsAppNumber,
        supportMessage: settings.supportMessage,
        isActive: settings.isActive,
      });
      showSuccessToast("Support settings saved");
      fetchSettings();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save support settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Preloader />}

      <div className="container-fluid px-4 py-4">
        <h4 className="mb-1">Support Details</h4>
         
        <form onSubmit={handleSave}>
          <div className="card mb-4">
            <div className="card-header fw-semibold">Display</div>
            <div className="card-body">
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  checked={settings.isActive}
                  onChange={(e) => setSettings({ ...settings, isActive: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Show support information on POS devices
                </label>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <FormInput
                    label="Support title"
                    type="text"
                    value={settings.supportTitle}
                    onchange={(e) => setSettings({ ...settings, supportTitle: e.target.value })}
                  />
                </div>
                <div className="col-12">
                  <FormInput
                    label="Support message"
                    type="text"
                    value={settings.supportMessage}
                    onchange={(e) => setSettings({ ...settings, supportMessage: e.target.value })}
                  />
                  <small className="text-muted">Short description shown to POS users.</small>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header fw-semibold">Contact</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <FormInput
                    label="Support email"
                    type="email"
                    value={settings.supportEmail}
                    onchange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  />
                  <small className="text-muted">Shown on POS / AltePos support screens.</small>
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Go Fatafat support email"
                    type="email"
                    value={settings.goFatafatSupportEmail}
                    onchange={(e) => setSettings({ ...settings, goFatafatSupportEmail: e.target.value })}
                  />
                  <small className="text-muted">Shown in Go Fatafat marketplace customer emails.</small>
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Support phone"
                    type="text"
                    value={settings.supportPhone}
                    onchange={(e) => setSettings({ ...settings, supportPhone: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="WhatsApp number"
                    type="text"
                    value={settings.whatsAppNumber}
                    onchange={(e) => setSettings({ ...settings, whatsAppNumber: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Support hours"
                    type="text"
                    value={settings.supportHours}
                    onchange={(e) => setSettings({ ...settings, supportHours: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header fw-semibold">Links</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <FormInput
                    label="Website URL"
                    type="text"
                    value={settings.supportWebsite}
                    onchange={(e) => setSettings({ ...settings, supportWebsite: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Help portal URL"
                    type="text"
                    value={settings.helpUrl}
                    onchange={(e) => setSettings({ ...settings, helpUrl: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary">
            Save settings
          </button>
        </form>
      </div>
    </>
  );
};

export default SupportSettings;
